<template>
  <b-card
    class="file-sidebar"
    :class="{ grouped: hasProjects }"
    body-class="p-2"
  >
    <!-- Company files and project files are two separate lists: one switch, one list at a time. -->
    <div
      v-if="hasProjects"
      class="sb-tabs"
      role="tablist"
      aria-label="Which files"
      data-test="sidebar-tabs"
    >
      <button
        type="button"
        role="tab"
        class="sb-tab"
        :class="{ active: tab === 'company' }"
        :aria-selected="tab === 'company' ? 'true' : 'false'"
        data-test="tab-company"
        @click="chooseTab('company')"
      >
        Company
      </button>
      <button
        type="button"
        role="tab"
        class="sb-tab"
        :class="{ active: tab === 'projects' }"
        :aria-selected="tab === 'projects' ? 'true' : 'false'"
        data-test="tab-projects"
        @click="chooseTab('projects')"
      >
        Projects
      </button>
    </div>

    <!-- Company files -->
    <div v-show="showCompany" role="tabpanel" data-test="company-panel">
      <div v-for="(section, index) in fixedSections" :key="section.id">
        <hr v-if="index > 0" class="sb-divider" />

        <button
          v-for="item in section.items"
          :key="item.id"
          type="button"
          class="sidebar-item"
          :class="{ 'selected-item': selectedFolder === item.id }"
          @click="selectFolder(item.id)"
        >
          <span class="sb-icon" aria-hidden="true">
            <b-icon :icon="iconFor(item.id)" />
          </span>
          <span class="item-label">{{ item.label }}</span>
          <span :class="['item-count', { 'is-zero': item.count === 0 }]">{{
            item.count
          }}</span>
        </button>
      </div>

      <hr class="sb-divider" />

      <!-- User-created folders -->
      <div class="folders-head" :class="{ 'is-sub': hasProjects }">
        <button
          type="button"
          class="folders-toggle"
          :aria-expanded="foldersOpen ? 'true' : 'false'"
          data-test="folders-toggle"
          @click="foldersOpen = !foldersOpen"
        >
          <span class="sb-label">Folders</span>
          <span class="sb-label-count">{{ folders.length }}</span>
          <b-icon
            :icon="foldersOpen ? 'chevron-up' : 'chevron-down'"
            font-scale="0.75"
            aria-hidden="true"
          />
        </button>
        <button
          v-if="canManageFolders"
          type="button"
          class="icon-button"
          aria-label="New folder"
          @click="$emit('new-folder')"
        >
          +
        </button>
      </div>

      <div v-show="foldersOpen" data-test="folders-body">
        <div
          v-if="folders.length === 0 && canManageFolders"
          class="folders-empty"
        >
          <p class="folders-hint">
            Create folders to organise your files and locate them with ease.
          </p>
          <b-button
            variant="primary"
            size="sm"
            block
            class="mb-2"
            @click="$emit('suggest-folders')"
          >
            Add suggested folders
          </b-button>
          <b-button
            variant="outline-secondary"
            size="sm"
            block
            @click="$emit('new-folder')"
          >
            + New folder
          </b-button>
        </div>

        <b-form-input
          v-if="folders.length > SEARCH_THRESHOLD"
          v-model="folderSearch"
          size="sm"
          class="project-search"
          placeholder="Search folders"
          aria-label="Search folders"
          data-test="folder-search"
        />

        <div class="folder-list" data-test="folder-list">
          <div
            v-for="folder in visibleFolders"
            :key="folder.id"
            class="folder-item"
            :class="{
              selected: selectedFolder === folder.id,
              'drop-target': dropFolderId === folder.id,
            }"
            @dragover="onDragOver"
            @dragenter="dropFolderId = folder.id"
            @dragleave="onDragLeave($event, folder.id)"
            @drop.prevent="onDrop($event, folder)"
          >
            <button
              v-if="folder.hasChildren && !searching"
              type="button"
              class="folder-caret"
              :style="{ left: folder.depth * INDENT + 4 + 'px' }"
              :aria-expanded="isCollapsed(folder.id) ? 'false' : 'true'"
              :aria-label="
                (isCollapsed(folder.id) ? 'Show' : 'Hide') +
                ' folders inside ' +
                folder.label
              "
              data-test="folder-caret"
              @click.stop="toggleCollapsed(folder.id)"
            >
              <b-icon
                :icon="
                  isCollapsed(folder.id) ? 'chevron-right' : 'chevron-down'
                "
                font-scale="0.7"
              />
            </button>
            <button
              type="button"
              class="sidebar-item"
              :class="{ 'selected-item': selectedFolder === folder.id }"
              :style="rowStyle(folder)"
              data-test="folder-row"
              @click="selectFolder(folder.id)"
            >
              <span
                class="folder-icon"
                :style="iconStyle(folder.color)"
                aria-hidden="true"
              >
                <b-icon icon="folder-fill" />
              </span>
              <span class="item-label">{{
                searching ? folder.path : folder.label
              }}</span>
              <span
                :class="['item-count', { 'is-zero': folder.count === 0 }]"
                >{{ folder.count }}</span
              >
            </button>
            <b-dropdown
              size="sm"
              variant="transparent"
              no-caret
              right
              boundary="window"
              :popper-opts="{ positionFixed: true }"
              class="folder-menu"
              :aria-label="'Options for ' + folder.label"
            >
              <template #button-content>
                <b-icon icon="three-dots" />
              </template>
              <b-dropdown-item-button
                v-if="canManageFolders && folder.depth + 1 < MAX_LEVELS"
                data-test="new-subfolder"
                @click="$emit('new-folder', folder)"
              >
                New sub-folder
              </b-dropdown-item-button>
              <b-dropdown-item-button
                v-if="canManageFolders"
                @click="$emit('rename-folder', folder)"
              >
                Edit
              </b-dropdown-item-button>
              <b-dropdown-item-button @click="$emit('download-folder', folder)">
                Download as zip
              </b-dropdown-item-button>
              <b-dropdown-item-button
                v-if="canManageFolders"
                @click="$emit('delete-folder', folder)"
              >
                Delete
              </b-dropdown-item-button>
            </b-dropdown>
          </div>
          <p
            v-if="folders.length && !visibleFolders.length"
            class="folders-hint project-empty"
            data-test="folder-empty"
          >
            No folders match.
          </p>
        </div>
      </div>
    </div>

    <!-- Project files -->
    <div
      v-if="hasProjects"
      v-show="tab === 'projects'"
      role="tabpanel"
      data-test="projects-body"
    >
      <b-form-input
        v-if="projects.length > SEARCH_THRESHOLD"
        v-model="projectSearch"
        size="sm"
        class="project-search"
        :placeholder="'Search ' + projects.length + ' projects'"
        aria-label="Search projects"
        data-test="project-search"
      />

      <div class="project-list">
        <template v-for="group in projectGroups">
          <div
            v-if="projectGroups.length > 1"
            :key="group.key + '-label'"
            class="sb-group-label"
          >
            <span class="sb-label">{{ group.title }}</span>
            <span class="sb-label-count">{{ group.items.length }}</span>
          </div>
          <button
            v-for="project in group.items"
            :key="project.id"
            type="button"
            class="sidebar-item"
            :class="{ 'selected-item': selectedFolder === project.id }"
            :title="project.readOnly ? 'Shared with you (view only)' : ''"
            data-test="project-item"
            @click="selectFolder(project.id)"
          >
            <span class="folder-icon" aria-hidden="true">
              <b-icon :icon="project.readOnly ? 'people' : 'building'" />
            </span>
            <span class="item-label"
              >{{ project.label
              }}<template v-if="project.archived && projectGroups.length === 1">
                (archived)</template
              ></span
            >
            <span :class="['item-count', { 'is-zero': project.count === 0 }]">{{
              project.count
            }}</span>
          </button>
        </template>
        <p
          v-if="!visibleProjects.length"
          class="folders-hint project-empty"
          data-test="project-empty"
        >
          No projects match.
        </p>
      </div>
    </div>
  </b-card>
</template>

<script>
import { FILE_DRAG_TYPE } from '@/utils/fileDrag'
import { folderIconStyle } from '@/utils/folderColors'
import { ancestorIds, MAX_FOLDER_LEVELS } from '@/utils/folderTree'

const FOLDERS_OPEN_KEY = 'files.sidebar.foldersOpen'

// Whether the Folders list was left open; open unless the user folded it. Storage
// can be blocked or empty (private windows), so it never gets in the way.
function readOpen(key) {
  try {
    return window.localStorage.getItem(key) !== 'false'
  } catch (error) {
    return true
  }
}

function save(key, value) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch (error) {
    // Nothing to do: the choice still holds for this visit.
  }
}

// An icon per built-in view, so the list can be read at a glance.
const ICONS = {
  all: 'files',
  recent: 'clock-history',
  expiring: 'hourglass-split',
  received: 'inbox',
  sent: 'cursor',
}

export default {
  props: {
    selectedFolder: { type: String, default: 'all' },
    sections: { type: Array, default: () => [] },
    // False for a business admin whose role cannot edit files: they see folders but cannot change them.
    canManageFolders: { type: Boolean, default: true },
    // Projects whose files the user can open: { id, label, count, readOnly }.
    projects: { type: Array, default: () => [] },
  },

  data() {
    return {
      dropFolderId: null,
      foldersOpen: readOpen(FOLDERS_OPEN_KEY),
      // The last company view, so the Company tab can go back to it.
      lastCompanyView: 'all',
      projectSearch: '',
      folderSearch: '',
      // Folders folded shut in the tree (their sub-folders are hidden).
      collapsed: [],
      SEARCH_THRESHOLD: 6,
      INDENT: 16,
      MAX_LEVELS: MAX_FOLDER_LEVELS,
    }
  },

  watch: {
    foldersOpen(open) {
      save(FOLDERS_OPEN_KEY, open)
    },

    // Opening a folder unfolds the Folders list, and company views are remembered.
    selectedFolder: {
      immediate: true,
      handler(folderId) {
        if (!this.onProjectSide) this.lastCompanyView = folderId
        if (/^\d+$/.test(folderId)) {
          this.foldersOpen = true
          // The folder you open is never hidden inside a folded one.
          const parents = ancestorIds(this.folders, folderId)
          if (parents.some((id) => this.collapsed.includes(id))) {
            this.collapsed = this.collapsed.filter(
              (id) => !parents.includes(id),
            )
          }
        }
      },
    },
  },

  computed: {
    fixedSections() {
      return this.sections.filter((section) => section.id !== 'folders')
    },

    folders() {
      const section = this.sections.find((section) => section.id === 'folders')
      return section ? section.items : []
    },

    hasProjects() {
      return this.projects.length > 0
    },

    // The projects list or one project is open on the right.
    onProjectSide() {
      return (
        this.selectedFolder === 'projects' ||
        /^project:/.test(this.selectedFolder)
      )
    },

    // Follows the page, so the sidebar never disagrees with what is shown.
    tab() {
      return this.onProjectSide ? 'projects' : 'company'
    },

    // Without project files there is only the company list.
    showCompany() {
      return !this.hasProjects || this.tab === 'company'
    },

    searching() {
      return this.folderSearch.trim() !== ''
    },

    // Whether any folder has others inside it, so rows leave room for the fold-away arrow.
    nested() {
      return this.folders.some((folder) => folder.hasChildren)
    },

    // A search shows every match with its path; otherwise the tree, minus folded folders.
    visibleFolders() {
      const term = this.folderSearch.trim().toLowerCase()
      if (term) {
        return this.folders.filter((folder) =>
          folder.path.toLowerCase().includes(term),
        )
      }
      const hidden = new Set()
      return this.folders.filter((folder) => {
        const parent = folder.parentId
        if (parent && (hidden.has(parent) || this.collapsed.includes(parent))) {
          hidden.add(folder.id)
          return false
        }
        return true
      })
    },

    // A to Z, counting numbers as numbers ("project 2" before "project 10"), as on the projects list.
    visibleProjects() {
      const term = this.projectSearch.trim().toLowerCase()
      const matches = term
        ? this.projects.filter((project) =>
            project.label.toLowerCase().includes(term),
          )
        : this.projects.slice()
      return matches.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      )
    },

    // The company's own projects, then the ones shared in (view only), then archived ones.
    projectGroups() {
      const live = this.visibleProjects.filter((project) => !project.archived)
      return [
        {
          key: 'own',
          title: 'Your projects',
          items: live.filter((project) => !project.readOnly),
        },
        {
          key: 'shared',
          title: 'Shared with you',
          items: live.filter((project) => project.readOnly),
        },
        {
          key: 'archived',
          title: 'Archived',
          items: this.visibleProjects.filter((project) => project.archived),
        },
      ].filter((group) => group.items.length)
    },
  },

  methods: {
    iconStyle: folderIconStyle,

    isCollapsed(id) {
      return this.collapsed.includes(id)
    },

    toggleCollapsed(id) {
      this.collapsed = this.isCollapsed(id)
        ? this.collapsed.filter((item) => item !== id)
        : this.collapsed.concat(id)
    },

    // Sub-folders step in; every row leaves room for the arrow once any folder has one.
    rowStyle(folder) {
      const depth = this.searching ? 0 : folder.depth
      const base = this.nested && !this.searching ? 32 : 12
      return { paddingLeft: base + depth * this.INDENT + 'px' }
    },

    iconFor(id) {
      return ICONS[id] || 'file-earmark'
    },

    selectFolder(folderId) {
      this.$emit('folder-change', folderId)
    },

    // Company returns to the last company view; Projects opens the projects list
    // (also from inside a project, as a way back to it).
    chooseTab(tab) {
      if (tab === 'company') {
        if (this.tab === 'projects') this.selectFolder(this.lastCompanyView)
      } else if (this.selectedFolder !== 'projects') {
        this.selectFolder('projects')
      }
    },

    isFileDrag(event) {
      return Array.from(event.dataTransfer.types || []).includes(FILE_DRAG_TYPE)
    },

    onDragOver(event) {
      if (!this.isFileDrag(event)) return
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },

    onDragLeave(event, folderId) {
      if (event.currentTarget.contains(event.relatedTarget)) return
      if (this.dropFolderId === folderId) this.dropFolderId = null
    },

    onDrop(event, folder) {
      this.dropFolderId = null
      if (!this.canManageFolders || !this.isFileDrag(event)) return
      const ids = JSON.parse(event.dataTransfer.getData(FILE_DRAG_TYPE) || '[]')
      this.$emit('drop-files', { folderId: folder.id, ids })
    },
  },
}
</script>

<style scoped>
/* Outer card */
.file-sidebar {
  background-color: #ffffff;
  border: 1px solid #e1e5ea;
  border-radius: 14px;
}

/* ---- Company | Projects switch ---------------------------------------- */
.sb-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 10px;
  padding: 3px;
  background-color: #f3f4f6;
  border-radius: 10px;
}

.sb-tab {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 10px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #6b7280;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
}

.sb-tab:hover:not(.active) {
  color: #111827;
}

.sb-tab.active {
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(17, 24, 39, 0.12);
  color: #111827;
  font-weight: 700;
}

/* ---- Rows --------------------------------------------------------------- */
.file-sidebar .sidebar-item {
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  height: auto;
  min-height: 40px;
  padding: 8px 12px;
  margin: 1px 0;

  background-color: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  outline: none;

  color: #111827;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  text-decoration: none;
  box-shadow: none;
}

.item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Counts are small pills, so a number never looks like part of the name. */
.item-count {
  flex-shrink: 0;
  min-width: 26px;
  margin-left: 8px;
  padding: 0 8px;
  background-color: #f3f4f6;
  border-radius: 999px;
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}

/* An empty folder or project reads as quiet, not as a number to check. */
.item-count.is-zero {
  background-color: transparent;
  color: #d1d5db;
  font-weight: 400;
}

.file-sidebar .sidebar-item:hover:not(.selected-item) {
  background-color: #f3f4f6;
  color: #111827;
}

/* Selected: tinted row with a brand-blue bar down its left edge. */
.file-sidebar .sidebar-item.selected-item,
.file-sidebar .sidebar-item.selected-item:hover,
.file-sidebar .sidebar-item.selected-item:focus,
.file-sidebar .sidebar-item.selected-item:active {
  background-color: #eef4ff;
  box-shadow: inset 3px 0 0 #006cff;
  color: #006cff;
  font-weight: 700;
}

.file-sidebar .sidebar-item.selected-item .item-count {
  background-color: #dbe8ff;
  color: #006cff;
}

.file-sidebar .sidebar-item.selected-item .item-count.is-zero {
  background-color: transparent;
  color: #9db9f2;
}

.file-sidebar .sidebar-item:focus {
  outline: none;
}

.file-sidebar .sidebar-item:focus-visible {
  box-shadow: inset 0 0 0 2px #006cff;
}

.sb-icon,
.folder-icon {
  display: inline-flex;
  flex-shrink: 0;
  margin-right: 12px;
  color: #6b7280;
}

.folder-icon {
  color: #9ca3af;
}

.file-sidebar .sidebar-item.selected-item .sb-icon,
.file-sidebar .sidebar-item.selected-item .folder-icon {
  color: #006cff;
}

.sb-divider {
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 8px 4px;
}

/* ---- Section labels (Folders, Your projects, Shared with you) ------------ */
.folders-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 2px 2px 4px;
}

.sb-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.sb-label-count {
  margin-left: 6px;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 400;
}

.folders-toggle {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  padding: 6px 8px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #6b7280;
  font-family: inherit;
  cursor: pointer;
}

.folders-toggle svg {
  margin-left: 6px;
}

.folders-toggle:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.sb-group-label {
  display: flex;
  align-items: center;
  padding: 10px 12px 4px;
}

.sb-group-label:first-child {
  padding-top: 4px;
}

.icon-button {
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: #374151;
  font-family: inherit;
  font-size: 18px;
  line-height: 1;
}

.icon-button:hover {
  background-color: #f3f4f6;
}

/* ---- Lists, search, empty states ---------------------------------------- */
/* The app's inputs are tall; in a sidebar this one should match the rows. */
.project-search {
  height: 36px;
  margin: 4px 0 6px;
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
}

/* Many folders or projects scroll inside the sidebar instead of stretching the page. */
.folder-list,
.project-list {
  max-height: 320px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.project-empty {
  padding: 4px 12px;
  margin: 0;
}

.folders-empty {
  padding: 6px 12px 10px;
}

.folders-hint {
  margin-bottom: 10px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.4;
}

/* ---- Folder rows: drop target and menu ---------------------------------- */
.folder-item {
  position: relative;
}

.folder-caret {
  position: absolute;
  top: 50%;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  transform: translateY(-50%);
}

.folder-caret:hover {
  background-color: #e5e7eb;
}

.folder-item.drop-target .sidebar-item {
  background-color: #e8f0ff;
  box-shadow: inset 0 0 0 2px #006cff;
}

/* BootstrapVue adds .position-static when the menu escapes the scrolling list
   (boundary="window"), so this has to win. No transform here: it would become
   the containing block of the fixed-position menu and push it off. */
.folder-item .folder-menu {
  position: absolute !important;
  top: 50%;
  right: 4px;
  margin-top: -15px;
  opacity: 0;
}

.folder-menu.show {
  z-index: 5;
}

.folder-menu.show,
.folder-item:hover .folder-menu,
.folder-item:focus-within .folder-menu {
  opacity: 1;
}

/* The menu takes the count's place while it is showing. */
.folder-item:hover .item-count,
.folder-item:focus-within .item-count {
  visibility: hidden;
}

/* No hover on touch screens, so keep the menu visible and leave room for it. */
@media (hover: none) {
  .folder-menu {
    opacity: 1;
  }

  .folder-item .sidebar-item {
    padding-right: 44px;
  }

  .folder-item .item-count {
    visibility: visible;
  }
}
</style>
